import React from 'react';

import { Alert, Button, Card, Descriptions, Empty, Flex, Result, Skeleton, Typography } from 'antd';
import { useTranslation } from 'react-i18next';

import { NSKeys } from '@app/i18n';
import { LegalEntitySubjectType, RetrieveLegalEntitySchema, ScopeLegalEntityStatus } from '@app/models/users';
import { isForbiddenError } from '@app/services/apiErrors';
import { formatDate } from '@app/services/date';
import { useApiErrorMessage } from '@app/services/hooks/useApiErrorMessage';
import { useLocalSelector } from '@app/services/hooks/useRedux';
import { stringToBoolean } from '@app/services/stringToBoolean';
import { AccessDenied } from '@components/AccessDenied';

import { useLegalEntityQuery } from '../../hooks';
import { LegalEntityStatusTag } from '../LegalEntityStatusTag';
import { Poas } from '../Poas';

import { EdoProfile } from './components/EdoProfile';

export const OrganizationContainer: React.FC = () => {
  const scope = useLocalSelector((state) => state.currentSession.data?.scope);
  const spaceT = useTranslation(NSKeys.spaceSettings);
  const commonT = useTranslation(NSKeys.common);
  const getErrorMessage = useApiErrorMessage();
  const legalEntityUid = scope?.legal_entity_uid;
  const card = useLegalEntityQuery(legalEntityUid);

  if (!legalEntityUid) {
    return <Empty description={spaceT.t('organization.notLinked')} />;
  }
  if (card.isLoading) {
    return <Skeleton active />;
  }
  if (isForbiddenError(card.error)) {
    return <AccessDenied withProfileLink={false} />;
  }
  if (!card.data) {
    return (
      <Result
        status="error"
        subTitle={getErrorMessage(card.error)}
        extra={<Button onClick={() => card.refetch()}>{commonT.t('buttonReload')}</Button>}
      />
    );
  }

  const legalEntity: RetrieveLegalEntitySchema = card.data;
  const isEntrepreneur = legalEntity.subject_type === LegalEntitySubjectType.IndividualEntrepreneur;

  return (
    <Flex vertical gap={24}>
      {scope?.is_sandbox && (
        <Alert
          type="warning"
          showIcon
          title={spaceT.t('organization.sandboxTitle')}
          description={
            legalEntity.status === ScopeLegalEntityStatus.Rejected
              ? spaceT.t('organization.sandboxRejected')
              : spaceT.t('organization.sandboxInReview')
          }
        />
      )}
      <Descriptions
        title={legalEntity.name}
        extra={<LegalEntityStatusTag status={legalEntity.status} />}
        bordered
        size="small"
        column={1}
        items={[
          { key: 'full_name', label: spaceT.t('organization.fullName'), children: legalEntity.full_name || '—' },
          {
            key: 'subject_type',
            label: spaceT.t('organization.subjectType'),
            children: legalEntity.subject_type ? spaceT.t(`subjectType.${legalEntity.subject_type}` as const) : '—',
          },
          { key: 'inn', label: commonT.t('inn'), children: legalEntity.inn },
          ...(isEntrepreneur
            ? []
            : [{ key: 'kpp', label: spaceT.t('organization.kpp'), children: legalEntity.kpp || '—' }]),
          {
            key: 'ogrn',
            label: isEntrepreneur ? spaceT.t('organization.ogrnip') : spaceT.t('organization.ogrn'),
            children: legalEntity.ogrn || '—',
          },
          {
            key: 'registration_date',
            label: spaceT.t('organization.registrationDate'),
            children: formatDate(legalEntity.registration_date),
          },
          {
            key: 'legal_address',
            label: spaceT.t('organization.legalAddress'),
            children: legalEntity.legal_address?.as_string || '—',
          },
          ...(legalEntity.actual_address?.as_string
            ? [
                {
                  key: 'actual_address',
                  label: spaceT.t('organization.actualAddress'),
                  children: legalEntity.actual_address.as_string,
                },
              ]
            : []),
        ]}
      />
      <Typography.Text type="secondary">{spaceT.t('organization.registryHint')}</Typography.Text>
      <EdoProfile legalEntityUid={legalEntityUid} />
      {stringToBoolean(process.env.REACT_APP_FEATURE_EPD) && (
        <Card size="small" title={spaceT.t('poa.title')}>
          <Poas target={{ kind: 'legalEntity', uid: legalEntityUid }} />
        </Card>
      )}
    </Flex>
  );
};
